import { CopyOutlined } from "@ant-design/icons";
import { Button, Drawer, Flex, Input, message } from "antd";
import { NodeFormData } from "components/CC/utils/nodeDefaults.ts";
import {
    formatBlueprintFormDataIssue,
    normalizeNodeFormData,
    validateNodeFormData,
} from "components/CC/utils/validateBlueprintFormData.ts";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useCallback, useEffect, useState } from "react";

interface NodeBlueprintDrawerProps {
    open: boolean;
    onClose: () => void;
    nodeType: string;
    formData: NodeFormData | undefined;
    onApply: (formData: NodeFormData) => void;
}

interface ValidationOutcome {
    valid: boolean;
    formData?: NodeFormData;
    message?: string;
}

export const NodeBlueprintDrawer = ({ open, onClose, nodeType, formData, onApply }: NodeBlueprintDrawerProps) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tGlobal } = usePaldockTranslation();
    const [content, setContent] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Validates JSON structure + node data shape. Shared by the initial-open sync, live typing,
    // and Apply — so a step that's already invalid (e.g. left half-filled-in) can't be applied
    // just because the user never touched the editor.
    const validateContent = useCallback(
        (newContent: string): ValidationOutcome => {
            try {
                const parsed = JSON.parse(newContent);

                if (!parsed.nodeType || typeof parsed.nodeType !== "string") {
                    const errorMessage = t("blueprint.error_invalid_node_type");
                    setIsValid(false);
                    setError(errorMessage);
                    return { valid: false, message: errorMessage };
                }

                if (parsed.nodeType !== nodeType) {
                    const errorMessage = t("blueprint.error_node_type_mismatch", {
                        expected: nodeType,
                        actual: parsed.nodeType,
                    });
                    setIsValid(false);
                    setError(errorMessage);
                    return { valid: false, message: errorMessage };
                }

                if (!parsed.formData || typeof parsed.formData !== "object") {
                    const errorMessage = t("blueprint.error_invalid_form_data");
                    setIsValid(false);
                    setError(errorMessage);
                    return { valid: false, message: errorMessage };
                }

                const issues = validateNodeFormData(parsed.nodeType, parsed.formData);
                if (issues.length > 0) {
                    const detail = formatBlueprintFormDataIssue(tGlobal, issues[0]);
                    const suffix =
                        issues.length > 1
                            ? ` ${t("blueprint.error_invalid_data_more", { count: issues.length - 1 })}`
                            : "";
                    const errorMessage = `${t("blueprint.error_invalid_step_data", { details: detail })}${suffix}`;
                    setIsValid(false);
                    setError(errorMessage);
                    return { valid: false, message: errorMessage };
                }

                setIsValid(true);
                setError(null);
                return {
                    valid: true,
                    formData: normalizeNodeFormData(parsed.nodeType, parsed.formData) as NodeFormData,
                };
            } catch {
                const errorMessage = t("blueprint.error_invalid_json");
                setIsValid(false);
                setError(errorMessage);
                return { valid: false, message: errorMessage };
            }
        },
        [t, tGlobal, nodeType]
    );

    // Sync internal state with external value when drawer opens — validated immediately, since
    // the current step data can already be invalid before the user edits anything.
    useEffect(() => {
        if (open) {
            const exportData = {
                nodeType,
                formData: formData || {},
            };
            const json = JSON.stringify(exportData, null, 2);
            setContent(json);
            validateContent(json);
        }
    }, [open, nodeType, formData]);

    const handleContentChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newContent = e.target.value;
            setContent(newContent);
            validateContent(newContent);
        },
        [validateContent]
    );

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(content);
        message.success(t("blueprint.copied"));
    }, [content, t]);

    const handleApply = useCallback(() => {
        const result = validateContent(content);
        if (!result.valid || !result.formData) {
            message.error(result.message ?? t("blueprint.error_invalid_json"));
            return;
        }
        onApply(result.formData);
        onClose();
        message.success(t("blueprint.applied"));
    }, [content, validateContent, onApply, onClose, t]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={<DrawerTitle mainTitle={t("blueprint.node_title")} />}
            width={600}
            extra={
                <Flex gap="small">
                    <Button size="large" icon={<CopyOutlined />} onClick={handleCopy}>
                        {tC("copy")}
                    </Button>
                    <Button size="large" onClick={onClose}>
                        {tC("cancel")}
                    </Button>
                    <Button size="large" type="primary" onClick={handleApply} disabled={!isValid}>
                        {tC("apply")}
                    </Button>
                </Flex>
            }
        >
            <DrawerContent>
                <p className="text-gray-500 mb-4">{t("blueprint.node_description")}</p>
                <Input.TextArea
                    value={content}
                    onChange={handleContentChange}
                    rows={20}
                    status={!isValid ? "error" : undefined}
                    style={{ fontFamily: "monospace", fontSize: 12 }}
                />
                {error && <p className="text-red-500 mt-2">{error}</p>}
            </DrawerContent>
        </Drawer>
    );
};
